// The custom glass (?glass=custom; js/glass/look.js): the resin's
// two-interface refraction at one buffer lookup per pixel.
//
// It keeps MeshTransmissionMaterial's render protocol (js/cross-scene.js
// render): the scene without the glass goes into a buffer; the glass's back
// faces refract that buffer into a second one (the far side of the cross, so
// the ridges behind the ridges survive); the front faces refract the second
// buffer onto the screen. What differs is how a pass reads its buffer.
// MeshTransmissionMaterial takes N jittered samples per pixel (each a
// refraction through a randomly tilted normal, per color channel) and
// averages them: the frost is the spread of those samples, and its grain is
// their noise. Here the frost is in the buffer instead: the buffers are
// mipmapped (three generates the chain after each pass renders into one),
// and each pixel reads the level whose texels are as wide as the frost's
// spread at that point, with a B-spline bicubic filter (smooth across texels
// and levels: eight bilinear taps), jittered within the footprint by the
// grain. The footprint is physical in shape: where the exit moves when the
// normal tilts by the frost — wider through more resin, narrower further
// from the camera, and wider and stretched where the surface is seen
// obliquely (the refraction bends more there), as MTM's samples spread;
// where the refracted ray leaves the screen it reads the buffer's edge, nearly
// as sharp as MeshTransmissionMaterial's clamped samples do.
//
// The shading is MeshTransmissionMaterial's (a MeshPhysicalMaterial whose
// diffuse is replaced by the refraction), written out: transmission × (1 −
// Fresnel) × Beer's-law tint × the cavity map, plus the environment's
// reflection (split-sum GGX, occluded by the AO map) and the two directional
// lights' GGX highlights, plus the LED glow's emission. Left out, being below
// visibility for a dielectric this smooth: the multiple-scattering term of
// the reflections (under 0.1% of a pixel's light here).
//
// One program per configuration. It is a RawShaderMaterial so that three's
// program cache key holds only its defines: the back pass (into a linear
// half-float buffer) and the front pass (to the sRGB, ACES-tone-mapped
// canvas) share it. Which side a pass draws, and whether it writes to the
// screen, are uniforms; the screen's tone mapping and sRGB encoding are
// applied here, explicitly, matching three's for renderer.toneMapping
// ACESFilmicToneMapping (checked: any other mapping throws). Defines change
// only with the quality knobs that compile (the maps, dispersion,
// distortion off) and the environment map's size.
//
// No isnan()/isinf() anywhere (tests/e2e/shaders.e2e.mjs): on ANGLE→Metal
// either disables fast math for the whole shader.
import * as THREE from "three";
import { LOOK_DEFAULTS, resolveLook } from "./look.js";
import { FIELD_ATLAS_PARS } from "../distortion-field.js";

const VERTEX = /* glsl */ `
precision highp float;
precision highp int;

uniform mat4 modelMatrix;
uniform mat4 viewMatrix;
uniform mat4 projectionMatrix;
uniform mat3 normalMatrix;
uniform float side; // 1: front faces; -1: back faces (normals flipped to face the camera)

in vec3 position;
in vec3 normal;
in vec2 uv;

out vec3 vWorldPosition;
out vec3 vWorldNormal;
out vec2 vUv;

void main() {
  vec4 world = modelMatrix * vec4( position, 1.0 );
  vWorldPosition = world.xyz;
  // three's: the view-space normal, back to world space (the model's scale
  // is uniform)
  vWorldNormal = side * ( vec4( normalMatrix * normal, 0.0 ) * viewMatrix ).xyz;
  vUv = uv;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const FRAGMENT = /* glsl */ `
precision highp float;
precision highp int;

#define texture2D texture
#define texture2DGradEXT textureGrad
#define PI 3.141592653589793
#define RECIPROCAL_PI 0.3183098861837907
#define saturate( a ) clamp( a, 0.0, 1.0 )
float pow2( const in float x ) { return x * x; }

uniform mat4 modelMatrix;
uniform mat4 viewMatrix;
uniform mat4 projectionMatrix;
uniform vec3 cameraPosition;

// the pass
uniform sampler2D buffer;
uniform float thickness;
uniform float side; // 1: front faces; -1: back faces
uniform bool toScreen;
uniform float toneMappingExposure;

// the look (js/glass/look.js)
uniform float ior;
uniform float exitRefraction;
uniform float frost;
uniform float grain;
uniform float dispersion;
uniform float roughness;
uniform float reflection;
uniform float specular;
uniform vec3 attenuationCoefficient;
uniform vec2 normalScale;
uniform float distortion;
uniform float cavityStrength;
uniform float aoMapIntensity;

uniform sampler2D envMap;
uniform vec3 lightDirection[ 2 ];
uniform vec3 lightColor[ 2 ];

#ifdef USE_NORMALMAP
  uniform sampler2D normalMap;
  uniform mat3 normalMapTransform;
#endif
#ifdef USE_AOMAP
  uniform sampler2D aoMap;
#endif
#ifdef USE_MAP
  uniform sampler2D map;
#endif
#ifdef USE_DISTORTION
${FIELD_ATLAS_PARS}
#endif

// the LED glow (js/cross-scene.js GLOW_FRAGMENT: the same shading)
uniform sampler2D emissiveMap;
uniform float glowAmount;
uniform vec3 glowColor;
uniform vec3 glowWeights;
uniform float glowPeak;

in vec3 vWorldPosition;
in vec3 vWorldNormal;
in vec2 vUv;

out highp vec4 fragColor;

#include <cube_uv_reflection_fragment>

// --- three's BRDF pieces (bsdfs, lights_physical_pars_fragment), r169 ---
vec3 F_Schlick( const in vec3 f0, const in float f90, const in float dotVH ) {
  float fresnel = exp2( ( - 5.55473 * dotVH - 6.98316 ) * dotVH );
  return f0 * ( 1.0 - fresnel ) + ( f90 * fresnel );
}
float V_GGX_SmithCorrelated( const in float alpha, const in float dotNL, const in float dotNV ) {
  float a2 = pow2( alpha );
  float gv = dotNL * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNV ) );
  float gl = dotNV * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNL ) );
  return 0.5 / max( gv + gl, 1e-6 );
}
float D_GGX( const in float alpha, const in float dotNH ) {
  float a2 = pow2( alpha );
  float denom = pow2( dotNH ) * ( a2 - 1.0 ) + 1.0;
  return RECIPROCAL_PI * a2 / pow2( denom );
}
vec3 BRDF_GGX( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in vec3 f0, const in float rough ) {
  float alpha = pow2( rough );
  vec3 halfDir = normalize( lightDir + viewDir );
  float dotNL = saturate( dot( normal, lightDir ) );
  float dotNV = saturate( dot( normal, viewDir ) );
  float dotNH = saturate( dot( normal, halfDir ) );
  float dotVH = saturate( dot( viewDir, halfDir ) );
  return F_Schlick( f0, 1.0, dotVH ) * ( V_GGX_SmithCorrelated( alpha, dotNL, dotNV ) * D_GGX( alpha, dotNH ) );
}
// the split-sum's environment BRDF: reflectance = f0 * x + f90 * y
vec2 DFGApprox( const in vec3 normal, const in vec3 viewDir, const in float rough ) {
  float dotNV = saturate( dot( normal, viewDir ) );
  const vec4 c0 = vec4( - 1, - 0.0275, - 0.572, 0.022 );
  const vec4 c1 = vec4( 1, 0.0425, 1.04, - 0.04 );
  vec4 r = rough * c0 + c1;
  float a004 = min( r.x * r.x, exp2( - 9.28 * dotNV ) ) * r.x + r.y;
  return vec2( - 1.04, 1.04 ) * a004 + r.zw;
}
float specularOcclusion( const in float dotNV, const in float ao, const in float rough ) {
  return saturate( pow( dotNV + ao, exp2( - 16.0 * rough - 1.0 ) ) - 1.0 + ao );
}
mat3 tangentFrame( vec3 eyePos, vec3 N, vec2 uv ) {
  vec3 q0 = dFdx( eyePos );
  vec3 q1 = dFdy( eyePos );
  vec2 st0 = dFdx( uv );
  vec2 st1 = dFdy( uv );
  vec3 q1perp = cross( q1, N );
  vec3 q0perp = cross( N, q0 );
  vec3 T = q1perp * st0.x + q0perp * st1.x;
  vec3 B = q1perp * st0.y + q0perp * st1.y;
  float det = max( dot( T, T ), dot( B, B ) );
  float scale = ( det == 0.0 ) ? 0.0 : inversesqrt( det );
  return mat3( T * scale, B * scale, N );
}

// --- the blurred lookup: B-spline bicubic within a mip level, linear across
// levels (three's transmission_pars_fragment textureBicubic) ---
float w0( float a ) { return ( 1.0 / 6.0 ) * ( a * ( a * ( - a + 3.0 ) - 3.0 ) + 1.0 ); }
float w1( float a ) { return ( 1.0 / 6.0 ) * ( a * a * ( 3.0 * a - 6.0 ) + 4.0 ); }
float w2( float a ) { return ( 1.0 / 6.0 ) * ( a * ( a * ( - 3.0 * a + 3.0 ) + 3.0 ) + 1.0 ); }
float w3( float a ) { return ( 1.0 / 6.0 ) * ( a * a * a ); }
float g0( float a ) { return w0( a ) + w1( a ); }
float h0( float a ) { return - 1.0 + w1( a ) / ( w0( a ) + w1( a ) ); }
float h1( float a ) { return 1.0 + w3( a ) / ( w2( a ) + w3( a ) ); }
vec3 bicubicLevel( vec2 uv, float level ) {
  vec2 size = vec2( textureSize( buffer, int( level ) ) );
  vec2 texel = 1.0 / size;
  uv = uv * size + 0.5;
  vec2 iuv = floor( uv );
  vec2 fuv = fract( uv );
  float g0x = g0( fuv.x );
  float g0y = g0( fuv.y );
  float h0x = h0( fuv.x );
  float h1x = h1( fuv.x );
  float h0y = h0( fuv.y );
  float h1y = h1( fuv.y );
  vec2 p0 = ( vec2( iuv.x + h0x, iuv.y + h0y ) - 0.5 ) * texel;
  vec2 p1 = ( vec2( iuv.x + h1x, iuv.y + h0y ) - 0.5 ) * texel;
  vec2 p2 = ( vec2( iuv.x + h0x, iuv.y + h1y ) - 0.5 ) * texel;
  vec2 p3 = ( vec2( iuv.x + h1x, iuv.y + h1y ) - 0.5 ) * texel;
  return g0y * ( g0x * textureLod( buffer, p0, level ).rgb + ( 1.0 - g0x ) * textureLod( buffer, p1, level ).rgb )
    + ( 1.0 - g0y ) * ( g0x * textureLod( buffer, p2, level ).rgb + ( 1.0 - g0x ) * textureLod( buffer, p3, level ).rgb );
}
vec3 blurredLookup( vec2 uv, float lod ) {
  return mix( bicubicLevel( uv, floor( lod ) ), bicubicLevel( uv, ceil( lod ) ), fract( lod ) );
}

// The view ray refracted at the surface (normal n, facing the camera): a
// unit vector, or zero where it can't leave the resin (total internal
// reflection). MeshTransmissionMaterial refracts at every surface as if
// entering the resin from air (eta = 1/ior), the back faces too; there the
// ray is in fact leaving it (eta = ior), and past the critical angle it
// reflects instead. exitRefraction blends the back faces from the one to the
// other (0 = MTM's, 1 = physical; the front faces always enter).
float etaFor( float refractiveIndex ) {
  return side > 0.0 ? 1.0 / refractiveIndex : mix( 1.0 / refractiveIndex, refractiveIndex, exitRefraction );
}
vec3 refractedRay( vec3 v, vec3 n, float refractiveIndex ) {
  // refract() of unit vectors is a unit vector (or zero): no normalize
  return refract( - v, n, etaFor( refractiveIndex ) );
}
// where that ray has travelled the pass's thickness (in the model's units):
// world space (MeshTransmissionMaterial's getVolumeTransmissionRay)
vec3 refractedExit( vec3 pos, vec3 ray, vec3 modelScale ) {
  return pos + ray * thickness * modelScale;
}
vec2 toBuffer( vec3 world, out float w ) {
  vec4 clip = projectionMatrix * viewMatrix * vec4( world, 1.0 );
  w = clip.w;
  return clip.xy / clip.w * 0.5 + 0.5;
}

// a hash of the pixel (pcg): the grain's per-pixel jitter, fixed on screen
vec2 pixelNoise() {
  uvec2 p = uvec2( gl_FragCoord.xy );
  uint h = p.x * 1664525u + p.y * 22695477u + 1013904223u;
  h ^= h >> 16u; h *= 0x7feb352du; h ^= h >> 15u; h *= 0x846ca68bu; h ^= h >> 16u;
  uint k = h * 747796405u + 2891336453u;
  k ^= k >> 16u; k *= 0x7feb352du; k ^= k >> 15u;
  return vec2( h & 0xffffu, k & 0xffffu ) / 65535.0 - 0.5;
}

// three's ACESFilmicToneMapping and sRGB encoding (tonemapping_pars_fragment,
// colorspace_pars_fragment), r169
vec3 RRTAndODTFit( vec3 v ) {
  vec3 a = v * ( v + 0.0245786 ) - 0.000090537;
  vec3 b = v * ( 0.983729 * v + 0.4329510 ) + 0.238081;
  return a / b;
}
vec3 ACESFilmicToneMapping( vec3 color ) {
  const mat3 ACESInputMat = mat3(
    vec3( 0.59719, 0.07600, 0.02840 ),
    vec3( 0.35458, 0.90834, 0.13383 ),
    vec3( 0.04823, 0.01566, 0.83777 )
  );
  const mat3 ACESOutputMat = mat3(
    vec3( 1.60475, - 0.10208, - 0.00327 ),
    vec3( - 0.53108, 1.10813, - 0.07276 ),
    vec3( - 0.07367, - 0.00605, 1.07602 )
  );
  color *= toneMappingExposure / 0.6;
  color = ACESInputMat * color;
  color = RRTAndODTFit( color );
  color = ACESOutputMat * color;
  return saturate( color );
}
vec3 sRGBTransferOETF( vec3 value ) {
  return mix( pow( value, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), value * 12.92, vec3( lessThanEqual( value, vec3( 0.0031308 ) ) ) );
}

void main() {
  vec3 pos = vWorldPosition;
  vec3 v = normalize( cameraPosition - pos );
  vec3 geometryNormal = normalize( vWorldNormal );
  vec3 n = geometryNormal;
  #ifdef USE_NORMALMAP
    vec3 mapN = texture( normalMap, ( normalMapTransform * vec3( vUv, 1.0 ) ).xy ).xyz * 2.0 - 1.0;
    mapN.xy *= normalScale;
    n = normalize( tangentFrame( pos, geometryNormal, vUv ) * mapN );
  #endif
  // three's specular antialiasing: facets smaller than a pixel read rougher
  vec3 dxy = max( abs( dFdx( geometryNormal ) ), abs( dFdy( geometryNormal ) ) );
  float rough = min( max( roughness, 0.0525 ) + max( max( dxy.x, dxy.y ), dxy.z ), 1.0 );
  float dotNV = saturate( dot( n, v ) );
  float f0 = pow2( ( ior - 1.0 ) / ( ior + 1.0 ) );

  // --- transmission ---
  vec3 refractionNormal = n;
  #ifdef USE_DISTORTION
    refractionNormal = normalize( n + distortion * sampleDistortionAtlas( ( distortionToField * vec4( pos, 1.0 ) ).xyz ) );
  #endif
  vec3 modelScale = vec3( length( modelMatrix[ 0 ].xyz ), length( modelMatrix[ 1 ].xyz ), length( modelMatrix[ 2 ].xyz ) );
  #ifdef DISPERSION
    // the main lookup at mid-spectrum (green); red and blue are offset from it
    float centerIor = ior * ( 1.0 + 0.5 * dispersion );
  #else
    float centerIor = ior;
  #endif
  vec3 ray = refractedRay( v, refractionNormal, centerIor );
  // 1 where the ray is trapped (total internal reflection), else 0
  float trapped = step( 0.5, 1.0 - dot( ray, ray ) );
  vec3 exitPoint = refractedExit( pos, ray, modelScale );
  float w;
  vec2 exitUv = toBuffer( exitPoint, w );
  float pathLength = thickness * modelScale.x;
  vec2 bufferSize = vec2( textureSize( buffer, 0 ) );
  // The frost's footprint at the exit, in texels of the buffer's top level:
  // where the exit moves when the normal tilts along two tangent axes, by
  // the tilt that spreads the rays by frost face-on (MeshTransmissionMaterial
  // tilts the normal itself). Oblique, the same tilt bends the ray more, so
  // the footprint grows, and stretches across the incidence plane.
  // (at an IOR of 1 nothing bends, whatever the tilt: no frost)
  float tilt = frost * centerIor / max( centerIor - 1.0, 0.01 );
  vec3 tangent1 = normalize( cross( refractionNormal, abs( refractionNormal.x ) > 0.5 ? vec3( 0.0, 1.0, 0.0 ) : vec3( 1.0, 0.0, 0.0 ) ) );
  vec3 tangent2 = cross( refractionNormal, tangent1 );
  float w1, w2;
  vec2 footprint1 = ( toBuffer( refractedExit( pos, refractedRay( v, normalize( refractionNormal + tilt * tangent1 ), centerIor ), modelScale ), w1 ) - exitUv ) * bufferSize;
  vec2 footprint2 = ( toBuffer( refractedExit( pos, refractedRay( v, normalize( refractionNormal + tilt * tangent2 ), centerIor ), modelScale ), w2 ) - exitUv ) * bufferSize;
  // its radius (root mean square): face-on, frost × the refracted ray's
  // length, projected
  float spread = sqrt( 0.5 * ( dot( footprint1, footprint1 ) + dot( footprint2, footprint2 ) ) );
  // A ray that leaves the screen reads the buffer's edge (clamped), where
  // MeshTransmissionMaterial's samples land on the edge texels, sharp: the
  // blur reaches only as far inside as the frost spreads past the edge (a
  // blurred level's edge texels would average the image well inside it).
  // The grain keeps the whole spread: along the edge, and inside past it;
  // and the blur keeps at least a quarter of it (two levels finer), or the
  // grain's single lookups there would speckle (MTM averages six).
  vec2 halfTexel = 0.5 / bufferSize;
  float beyondEdge = length( ( exitUv - clamp( exitUv, halfTexel, 1.0 - halfTexel ) ) * bufferSize );
  // (blurredLookup reads the level above lod too: keep that one in the chain)
  float maxLod = floor( log2( max( bufferSize.x, bufferSize.y ) ) ) - 1.0;
  float lod = clamp( log2( max( max( spread - beyondEdge, 0.25 * spread ), 1.0 ) ), 0.0, maxLod );
  vec2 noise = pixelNoise();
  vec2 jitter = ( noise.x * footprint1 + noise.y * footprint2 ) * grain * 2.0 / bufferSize;
  vec2 uv = exitUv + jitter;
  vec3 transmitted = blurredLookup( uv, lod );
  #ifdef DISPERSION
    // red and blue: the shift of their refraction, read from the same level
    // (plain trilinear: their difference carries the shift, the bicubic
    // lookup the image)
    float wr, wb;
    vec2 uvR = toBuffer( refractedExit( pos, refractedRay( v, refractionNormal, ior ), modelScale ), wr ) + jitter;
    vec2 uvB = toBuffer( refractedExit( pos, refractedRay( v, refractionNormal, ior * ( 1.0 + dispersion ) ), modelScale ), wb ) + jitter;
    vec3 center = textureLod( buffer, uv, lod ).rgb;
    transmitted.r += textureLod( buffer, uvR, lod ).r - center.r;
    transmitted.b += textureLod( buffer, uvB, lod ).b - center.b;
    transmitted = max( transmitted, vec3( 0.0 ) );
  #endif
  // trapped, the light comes from where the reflected ray finally leaves:
  // the studio, taken as the environment in its direction. All of it is
  // reflected: no Fresnel loss.
  if ( trapped > 0.5 ) transmitted = textureCubeUV( envMap, reflect( - v, refractionNormal ), rough ).rgb * reflection;
  vec3 base = vec3( 1.0 );
  #ifdef USE_MAP
    base = mix( vec3( 1.0 ), texture( map, vUv ).rgb, cavityStrength );
  #endif
  vec2 fab = DFGApprox( refractionNormal, v, rough );
  float transmissionFresnel = f0 * fab.x + fab.y;
  vec3 color = ( 1.0 - transmissionFresnel * ( 1.0 - trapped ) ) * exp( - attenuationCoefficient * pathLength ) * transmitted * base;

  // --- reflections: the environment (split sum), then the lights ---
  vec3 reflected = normalize( mix( reflect( - v, n ), n, rough * rough ) );
  vec2 fabN = DFGApprox( n, v, rough );
  vec3 environment = textureCubeUV( envMap, reflected, rough ).rgb * reflection * ( f0 * fabN.x + fabN.y );
  #ifdef USE_AOMAP
    float ao = ( texture( aoMap, vUv ).r - 1.0 ) * aoMapIntensity + 1.0;
    environment *= specularOcclusion( dotNV, ao, rough );
  #endif
  color += environment;
  for ( int i = 0; i < 2; i++ ) {
    float dotNL = saturate( dot( n, lightDirection[ i ] ) );
    color += specular * dotNL * lightColor[ i ] * BRDF_GGX( lightDirection[ i ], v, n, vec3( f0 ), rough );
  }

  // --- the LED glow (see GLOW_FRAGMENT) ---
  vec3 glowBake = texture( emissiveMap, vUv ).rgb;
  float glowNdotV = abs( dot( n, v ) );
  float glowPath = 1.0 / max( glowNdotV, 0.25 );
  float glowEdgeOn = ( 1.0 - glowNdotV ) * ( 1.0 - glowNdotV );
  float glowLevel = glowWeights.x * glowBake.r * glowPath + glowWeights.y * glowBake.g + glowWeights.z * glowBake.b * glowEdgeOn;
  color += glowColor * glowAmount * glowPeak * ( 1.0 - exp( - glowLevel / glowPeak ) );

  // the buffers hold linear light; the screen, tone mapped sRGB
  if ( toScreen ) color = sRGBTransferOETF( ACESFilmicToneMapping( color ) );
  fragColor = vec4( color, 1.0 );
}
`;

// what the transmission buffers need to be for this glass to read them
export const BUFFER_TEXTURE = Object.freeze({
  generateMipmaps: true,
  minFilter: THREE.LinearMipmapLinearFilter,
});

// Creates the custom glass.
//   maps      { normalMap, aoMap, map }: the orange peel, AO and cavity bakes
//   glow      { uniforms: { glowAmount, glowColor, glowWeights }, peak, map }:
//             cross-scene's glow uniforms (shared: setGlow drives both
//             glasses) and its bake (or the placeholder until it loads)
//   distortion { toField }: the uniform holding the baked distortion
//             field's matrix (world -> its 0..1 coordinates; cross-scene
//             updates it with the pose); the field itself, an atlas
//             (js/distortion-field.js), comes by setDistortionAtlas
//   lights    the scene's directional lights (two)
//   look      the starting look (js/glass/look.js)
// Returns { material, look, setLook, setQuality, setGlowMap,
// setDistortionAtlas, setEnvironment, pass }:
//   pass(side, buffer, renderer, environment) — before a glass pass renders:
//     side "back" (into a buffer) or "front" (to the screen), reading
//     `buffer`; reflecting `environment` (a PMREM texture)
export function createCustomGlass({ maps, glow, distortion, lights, look = LOOK_DEFAULTS }) {
  if (lights.length !== 2 || !lights.every((l) => l.isDirectionalLight)) throw new Error("custom glass: expects the scene's two directional lights");
  const uniforms = {
    buffer: { value: null },
    side: { value: 1 },
    thickness: { value: 0 },
    toScreen: { value: false },
    toneMappingExposure: { value: 1 },
    ior: { value: 0 },
    exitRefraction: { value: 0 },
    frost: { value: 0 },
    grain: { value: 0 },
    dispersion: { value: 0 },
    roughness: { value: 0 },
    reflection: { value: 0 },
    specular: { value: 0 },
    attenuationCoefficient: { value: new THREE.Vector3() },
    normalScale: { value: new THREE.Vector2() },
    distortion: { value: 0 },
    cavityStrength: { value: 1 },
    aoMapIntensity: { value: 1 },
    envMap: { value: null },
    lightDirection: { value: [new THREE.Vector3(), new THREE.Vector3()] },
    lightColor: { value: [new THREE.Color(), new THREE.Color()] },
    normalMap: { value: maps.normalMap },
    normalMapTransform: { value: new THREE.Matrix3() },
    aoMap: { value: maps.aoMap },
    map: { value: maps.map },
    emissiveMap: { value: glow.map },
    glowPeak: { value: glow.peak },
    ...glow.uniforms,
    distortionToField: distortion.toField,
    distortionAtlas: { value: null },
    distortionAtlasDims: { value: new THREE.Vector4() },
  };
  const material = new THREE.RawShaderMaterial({
    name: "CustomGlass",
    glslVersion: THREE.GLSL3,
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    uniforms,
    // the screen pass tone maps and encodes itself (toScreen): three must
    // not, nor key the program on the target
    toneMapped: false,
  });

  let current = null;
  function setLook(next) {
    current = resolveLook(LOOK_DEFAULTS, next);
    const u = uniforms;
    u.ior.value = current.ior;
    u.exitRefraction.value = current.exitRefraction;
    u.frost.value = current.frost;
    u.grain.value = current.grain;
    u.dispersion.value = current.dispersion;
    u.roughness.value = current.roughness;
    u.reflection.value = current.reflection;
    u.specular.value = current.specular;
    u.normalScale.value.setScalar(current.normalStrength);
    u.distortion.value = distortionOff ? 0 : current.distortion;
    u.cavityStrength.value = current.cavity;
    u.aoMapIntensity.value = current.ao;
    // Beer's law: the tint (linear) is what is left after attenuationDistance
    const tint = new THREE.Color(current.tint);
    u.attenuationCoefficient.value.set(
      -Math.log(Math.max(tint.r, 1e-4)), -Math.log(Math.max(tint.g, 1e-4)), -Math.log(Math.max(tint.b, 1e-4)),
    ).divideScalar(current.attenuationDistance);
    return current;
  }

  // the quality knobs (js/quality.js) this glass compiles from; the
  // MTM-only ones (samples, dispersionStrength, cavityStrength) don't apply
  let distortionOff = false;
  const features = {};
  function setQuality(quality) {
    distortionOff = quality.distortionMode === "off";
    Object.assign(features, {
      USE_NORMALMAP: quality.orangePeel,
      USE_AOMAP: quality.ao,
      USE_MAP: quality.cavity,
      DISPERSION: quality.dispersion,
      // off: compiled out, not just weighted 0 (it is a texture lookup)
      USE_DISTORTION: !distortionOff,
    });
    updateDefines();
    setLook(current);
  }

  // The material's defines, rebuilt in one fixed order: three's program
  // cache key lists them in insertion order, so a define removed and added
  // back must land where it was, or the same program compiles again.
  let envDefines = {};
  function updateDefines() {
    const defines = { ...envDefines };
    for (const [name, on] of Object.entries(features)) if (on) defines[name] = "";
    if (JSON.stringify(defines) === JSON.stringify(material.defines)) return;
    material.defines = defines;
    material.needsUpdate = true;
  }

  function setGlowMap(texture) {
    uniforms.emissiveMap.value = texture;
  }

  // the field bakeDistortionField(..., { layout: "atlas" }) returns
  function setDistortionAtlas({ target, dims }) {
    uniforms.distortionAtlas.value = target.texture;
    uniforms.distortionAtlasDims.value.fromArray(dims);
  }

  const lightDir = new THREE.Vector3();
  function pass(side, buffer, renderer, environment) {
    if (side !== "back" && side !== "front") throw new Error(`custom glass: side must be "back" or "front", got ${side}`);
    if (renderer.toneMapping !== THREE.ACESFilmicToneMapping || renderer.outputColorSpace !== THREE.SRGBColorSpace) {
      throw new Error("custom glass: tone maps and encodes the screen pass itself, as ACES filmic to sRGB; the renderer is set otherwise");
    }
    if (!environment?.isTexture || !environment.image?.height) throw new Error("custom glass: needs the scene's PMREM environment map");
    if (buffer.minFilter !== BUFFER_TEXTURE.minFilter || !buffer.generateMipmaps) throw new Error("custom glass: its buffers must be mipmapped (BUFFER_TEXTURE)");
    const back = side === "back";
    material.side = back ? THREE.BackSide : THREE.FrontSide;
    uniforms.side.value = back ? -1 : 1;
    uniforms.thickness.value = back ? current.interiorThickness : current.thickness;
    uniforms.toScreen.value = !back;
    uniforms.toneMappingExposure.value = renderer.toneMappingExposure;
    uniforms.buffer.value = buffer;
    setEnvironment(environment);
    for (let i = 0; i < 2; i++) {
      const light = lights[i];
      light.updateMatrixWorld();
      light.target.updateMatrixWorld();
      uniforms.lightDirection.value[i].setFromMatrixPosition(light.matrixWorld)
        .sub(lightDir.setFromMatrixPosition(light.target.matrixWorld)).normalize();
      uniforms.lightColor.value[i].copy(light.color).multiplyScalar(light.intensity);
    }
    const normalMap = uniforms.normalMap.value;
    normalMap.updateMatrix();
    uniforms.normalMapTransform.value.copy(normalMap.matrix);
  }

  // three's CUBEUV_* defines for a PMREM texture `height` texels tall
  // (WebGLProgram generateCubeUVSize)
  function setEnvironment(texture) {
    uniforms.envMap.value = texture;
    const maxMip = Math.log2(texture.image.height) - 2;
    envDefines = {
      ENVMAP_TYPE_CUBE_UV: "",
      CUBEUV_TEXEL_WIDTH: 1 / (3 * Math.max(2 ** maxMip, 7 * 16)),
      CUBEUV_TEXEL_HEIGHT: 1 / texture.image.height,
      CUBEUV_MAX_MIP: `${maxMip}.0`,
    };
    updateDefines();
  }

  setLook(look);
  return {
    material, setLook, setQuality, setGlowMap, setDistortionAtlas, pass, setEnvironment,
    get look() { return current; },
  };
}
